import { Banner, Flex, Header, Loader, Tabs, Text } from '@components';
import InfoIcon from '@mui/icons-material/Info';
import { useUserQuery } from '@features/api/userSlice';
import Overview from '@pages/UserPortfolio/Overview/Overview';
import Transactions from '@pages/UserPortfolio/Transactions/Transactions';
import { createContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMatch, useNavigate } from 'react-router-dom';
import { Container, StyledHeaderContainer } from './PortfolioBase.styles';
import { SelectChangeEvent, useTheme } from '@mui/material';
import {
  useUserPortfolioQuery,
  useUserTransactionsQuery,
} from '@features/api/userPortfolioSlice';
import { TransactionStatus } from '@features/api/response/UserTransactions';
import { UserType } from '@features/api/response/User';
import NomineeInvestments from '@pages/NomineeInvestments/NomineeInvestments';
import { Reports } from '@pages/UserPortfolio/Reports/Reports';
import { COMBINED_CURRENCY, CurrencySelector } from '@pages/UserPortfolio/Overview/CurrencySelector';

export const CurrencyContext = createContext<{
  currency?: string;
  combinedCurrency?: string;
}>({});

const PortfolioBase = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const theme = useTheme();

  const match = useMatch('/portfolio/:lastPart');
  const tabValue = match?.params.lastPart;

  const { data: user, isLoading: isLoadingUser } = useUserQuery();
  const { data: initialTransactions, isLoading: isLoadingTransactions } =
    useUserTransactionsQuery(undefined, {
      skip: !user,
      refetchOnMountOrArgChange: true,
    });

  const [selectedTabId, setSelectedTabId] = useState(tabValue ?? 'overview');

  const { data: userPortfolio } = useUserPortfolioQuery();

  const [selectedCurrency, setSelectedCurrency] = useState<string>();
  const [selectedCombinedCurrency, setSelectedCombinedCurrency] =
    useState<string>();
  const [availableCurrencies, setAvailableCurrencies] = useState<string[]>();
  const [availableCombinedCurrencies, setAvailableCombinedCurrencies] =
    useState<string[]>();
  useEffect(() => {
    if (userPortfolio && userPortfolio.investmentData) {
      setSelectedCurrency(userPortfolio.investmentData[0].currencyCode);
      setSelectedCombinedCurrency(userPortfolio.investmentData[0].currencyCode);
      const currencies = userPortfolio.investmentData
        .filter((x) => !x.currencyCode.includes(COMBINED_CURRENCY))
        .map((x) => x.currencyCode);

      if (currencies.length > 1) {
        currencies.unshift(COMBINED_CURRENCY);
      }
      setAvailableCurrencies(currencies);
      setAvailableCombinedCurrencies(
        currencies.filter((x) => x !== COMBINED_CURRENCY)
      );
    }
  }, [userPortfolio]);

  useEffect(() => {
    if (tabValue) setSelectedTabId(tabValue);
  }, [tabValue]);

  const hasTransactionUnderReview =
    !!initialTransactions?.length &&
    initialTransactions?.every((x) => x.status === TransactionStatus.InReview);

  const getTabsData = useMemo(() => {
    if (!user) return [];
    return [
      {
        id: 'overview',
        label: intl.formatMessage({ id: 'portfolio.overview' }),
        component: <Overview />,
        tabProps: {
          onClick: () => {
            navigate('/portfolio/overview');
          },
          value: 'overview',
        },
      },
      {
        id: 'transactions',
        label: intl.formatMessage({
          id: 'portfolio.transactionsAndStatements',
        }),
        component:
          user.userType === UserType.NomineeCompanyHolder ||
          user.userType === UserType.NomineeAgent ? (
            <NomineeInvestments />
          ) : (
            <Transactions />
          ),
        tabProps: {
          onClick: () => {
            navigate('/portfolio/transactions');
          },
          value: 'transactions',
        },
      },
      {
        id: 'reports',
        label: intl.formatMessage({ id: 'portfolio.reportsTab' }),
        component: <Reports />,
        tabProps: {
          onClick: () => {
            navigate('/portfolio/reports');
          },
          value: 'reports',
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleChange = (_event: React.SyntheticEvent, newValue: string) => {
    setSelectedTabId(newValue);
  };

  const handleSelectedCurrencyChange = (e: SelectChangeEvent<string>) => {
    setSelectedCurrency(e.target.value);
    setSelectedCombinedCurrency(
      e.target.value === COMBINED_CURRENCY
        ? userPortfolio?.investmentData[0].currencyCode
        : undefined
    );
  };

  const handleSelectedCombinedCurrencyChange = (
    e: SelectChangeEvent<string>
  ) => {
    setSelectedCombinedCurrency(e.target.value);
  };

  if (isLoadingUser || isLoadingTransactions)
    return <Loader isLoading={isLoadingUser || isLoadingTransactions} />;

  return (
    <Container>
      <StyledHeaderContainer>
        <Header
          user={user}
          title={{
            name: intl.formatMessage({ id: 'menu.portfolio' }),
            to: '/portfolio',
          }}
          banner={
            hasTransactionUnderReview && selectedTabId !== 'transactions' ? (
              <Banner color='warning' icon={InfoIcon}>
                <Text
                  variant='h6'
                  fontWeight='fontWeightMedium'
                  color={theme.palette.primary.main}
                >
                  <FormattedMessage id='portfolio.transactions.reviewDisclaimer' />
                </Text>
              </Banner>
            ) : null
          }
        />
      </StyledHeaderContainer>

      <Flex height='100%'>
        <CurrencyContext.Provider
          value={{
            currency: selectedCurrency,
            combinedCurrency: selectedCombinedCurrency,
          }}
        >
          <Tabs
            variant='scrollable'
            allowScrollButtonsMobile
            tabItem={getTabsData}
            onChange={handleChange}
            value={selectedTabId}
            tabRightComponent={
              selectedTabId &&
              selectedTabId === 'overview' &&
              (availableCurrencies?.length ?? 0) > 1
                ? () => (
                    <CurrencySelector
                      selectedCurrency={selectedCurrency}
                      selectedCombinedCurrency={selectedCombinedCurrency}
                      availableCurrencies={availableCurrencies}
                      availableCombinedCurrencies={
                        availableCombinedCurrencies
                      }
                      handleSelectedCurrencyChange={
                        handleSelectedCurrencyChange
                      }
                      handleSelectedCombinedCurrencyChange={
                        handleSelectedCombinedCurrencyChange
                      }
                    />
                  )
                : undefined
            }
          />
        </CurrencyContext.Provider>
      </Flex>
    </Container>
  );
};

export default PortfolioBase;
